import React, { useEffect, useLayoutEffect, useState } from "react";
import cross from "../../assets/icons/cross.svg";
import img from "../../assets/login/login1.png";
import logo from "../../assets/logo.png";
import logowhite from "../../assets/logowhite.png";
import { useTheme } from "../../context/ThemeProvider";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import useAuth from "../../hooks/useAuth";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google"; // Correct usage
import { FcGoogle } from "react-icons/fc";
import { toast } from "react-toastify";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { normalizeEmail } from "../../utils/normalizeEmail";
import { stripSpaces, blockSpaceKey, handlePasteStripSpaces } from "../../utils/stripSpaces";
import { lockBodyScroll } from "../../utils/lockBodyScroll";
import AuthVerifyingOverlay from "../AuthVerifyingOverlay";
import ActiveLoginSessionsGate, {
  isMaxSessionsError,
} from "./ActiveLoginSessionsGate";

const SignInContent = ({ setShowModal, switchToSignUp, redirectAfterLogin }) => {
  const [loading, setLoading] = useState(false);
  /** Email/password login only — avoids blocking UI during Google / forgot-password flows */
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  /** Google OAuth: full-screen lock while backend verifies the token */
  const [isGoogleVerifying, setIsGoogleVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [deviceLimitState, setDeviceLimitState] = useState(null);

  // Forgot password states
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [resetEmail, setResetEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);

  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const location = useLocation();

  // Disable background scroll when modal is open
  useLayoutEffect(() => {
    return lockBodyScroll();
  }, []);

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpTimer]);

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const finishLoginWithToken = (accessToken) => {
    const decodedToken = jwtDecode(accessToken);
    const tokenData = {
      ...decodedToken,
      RawToken: accessToken,
    };
    setAuth(tokenData);

    if (redirectAfterLogin) {
      navigate(redirectAfterLogin);
    } else {
      const currentPath = location.pathname;
      if (
        currentPath === "/" ||
        currentPath.includes("search") ||
        currentPath.includes("category") ||
        currentPath.includes("collection") ||
        currentPath.includes("video")
      ) {
        navigate(currentPath);
      } else {
        navigate("/dashboard");
      }
    }

    setShowModal(false);
  };

  const openDeviceLimitGate = (data, pendingAuth = "password") => {
    setDeviceLimitState({
      sessions: data.activeLoginSessions || [],
      sessionManagementToken: data.sessionManagementToken || "",
      message: data.message,
      pendingAuth,
    });
    toast.info(
      data.message ||
        "You're already logged in on 2 devices. Please log out of one to continue.",
    );
  };

  const handleEmailChange = (e) => {
    const value = stripSpaces(e.target.value);
    setFormData({ ...formData, email: value });
    setErrors({ ...errors, email: "" });
  };

  const handleChange = (e) => {
    const value = stripSpaces(e.target.value);
    setFormData({ ...formData, [e.target.name]: value });
    setErrors({ ...errors, [e.target.name]: "" }); // Clear error when user types
  };

  const handleLogin = async () => {
    let newErrors = {};

    const email = formData.email.trim();
    const password = formData.password.trim();

    if (!email) newErrors.email = "Email is required";
    else if (!emailRegex.test(email)) newErrors.email = "Enter a valid email";
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 4)
      newErrors.password = "Password must be at least 4 characters";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      setIsAuthenticating(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/userlogin`,
        {
          id: normalizeEmail(formData.email),
          password: formData.password,
        },
      );
      const { accessToken } = response.data;
      finishLoginWithToken(accessToken);
    } catch (error) {
      console.error("Wrong Credentials", error);
      if (isMaxSessionsError(error)) {
        openDeviceLimitGate(error.response.data);
        return;
      }
      const status = error.response?.status;
      const data = error.response?.data;
      let errorMessage =
        data?.message ||
        "Invalid credentials. Please check your email and password.";
      if (status === 429 || data?.error === "RATE_LIMIT_EXCEEDED") {
        errorMessage = "Too many attempts. Please try again after 2 minutes.";
      }
      toast.error(errorMessage);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      console.log(
        "Google login SUCCESS credentialResponse:",
        credentialResponse,
      ); // ✅ Log 1
      try {
        setIsGoogleVerifying(true);
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/auth/google`,
          {
            credential: credentialResponse.access_token,
          },
        );
        console.log("Backend /auth/google RESPONSE:", response.data); // ✅ Log 2

        const { accessToken, user } = response.data;
        console.log("User data:", user);
        finishLoginWithToken(accessToken);
      } catch (error) {
        console.error("Error sending credential to backend:", error); // ✅ Log 4
        if (isMaxSessionsError(error)) {
          openDeviceLimitGate(error.response.data, "google");
          return;
        }
        const status = error.response?.status;
        const data = error.response?.data;
        let msg =
          data?.message || "Google login failed. Please try again.";
        if (
          status === 429 ||
          data?.error === "RATE_LIMIT_EXCEEDED"
        ) {
          msg =
            "Too many attempts. Please try again after 2 minutes.";
        }
        toast.error(msg);
        setErrors({
          ...errors,
          email: msg,
        });
      } finally {
        setIsGoogleVerifying(false);
      }
    },
    onError: (error) => {
      console.error("Google login FAILED:", error); // ✅ Log 5
      toast.error("Google login failed. Please try again.");
      setErrors({ ...errors, email: "Google login failed. Please try again." });
    },
    flow: "implicit",
  });

  // Forgot Password Functions
  const handleForgotPasswordClick = () => {
    setForgotPasswordMode(true);
    setForgotPasswordStep(1);
    setResetEmail("");
    setOtp(["", "", "", "", "", ""]);
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleBackToSignIn = () => {
    setForgotPasswordMode(false);
    setForgotPasswordStep(1);
    setResetEmail("");
    setOtp(["", "", "", "", "", ""]);
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSendOTP = async () => {
    if (!resetEmail.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/send-reset-otp`,
        { email: normalizeEmail(resetEmail) },
      );

      if (response.data.success) {
        toast.success("OTP sent to your email!");
        setForgotPasswordStep(2);
        setOtpTimer(600); // 10 minutes
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to send OTP. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(0, 1);
    }

    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      toast.error("Please enter the complete 6-digit OTP.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/verify-reset-otp`,
        { email: normalizeEmail(resetEmail), otp: otpString },
      );

      if (response.data.success) {
        toast.success("OTP verified successfully!");
        setForgotPasswordStep(3);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Invalid OTP. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      toast.error("Please enter a new password.");
      return;
    }

    const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      toast.error("Password must be at least 8 characters and include at least 1 uppercase letter and 1 special character.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      const otpString = otp.join("");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/reset-password`,
        {
          email: normalizeEmail(resetEmail),
          otp: otpString,
          newPassword,
        },
      );

      if (response.data.success) {
        toast.success("Password reset successfully! You can now sign in.");
        handleBackToSignIn();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to reset password. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/send-reset-otp`,
        { email: normalizeEmail(resetEmail) },
      );

      if (response.data.success) {
        toast.success("OTP resent to your email!");
        setOtp(["", "", "", "", "", ""]);
        setOtpTimer(600); // 10 minutes
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to resend OTP. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const uiLocked = isAuthenticating || isGoogleVerifying;

  return (
    <div
      id="signin-modal"
      className="bg-[#121212CC] h-screen fixed inset-0 z-50 modalOverflow"
      onClick={() => {
        if (!uiLocked) setShowModal(false);
      }}
    >
      {isGoogleVerifying && (
        <AuthVerifyingOverlay
          message="Logging you in"
          darkMode={darkMode}
        />
      )}
      <div className="bigscreen max-h-[100vh] h-[100vh] md:p-10">
        <div
          className={`overflow-y-scroll relative max-w-[1024px] mx-auto h-full max-h-full border p-5 pt-14 md:p-8 ${
            darkMode
              ? "bg-[#10130D] border-[#1E1E1E]"
              : "bg-white border-[#CBCBCB]"
          } shadow grid grid-cols-1 md:grid-cols-2 !gap-8`}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`h-8 absolute top-3 md:top-4 right-4 ${
              uiLocked ? "pointer-events-none opacity-40" : "cursor-pointer"
            }`}
            onClick={() => {
              if (!uiLocked) setShowModal(false);
            }}
          >
            <img src={cross} alt="close" className="h-full object-contain" />
          </div>

          <div className="h-full max-h-[150px] md:max-h-full w-full rounded-2xl">
            <img
              src={img}
              alt="Sign in"
              className="object-cover h-full max-h-full md:max-h-[calc(100vh-156px)] w-full rounded-2xl opacity-90"
            />
          </div>

          <div className="flex flex-col gap-4 justify-between py-4 md:py-12">
            <div className="h-6 md:h-8">
              <img
                src={darkMode ? logowhite : logo}
                alt="Shotkut logo"
                className="object-contain h-full"
              />
            </div>

            {!forgotPasswordMode && deviceLimitState ? (
              <ActiveLoginSessionsGate
                darkMode={darkMode}
                sessions={deviceLimitState.sessions}
                sessionManagementToken={deviceLimitState.sessionManagementToken}
                email={normalizeEmail(formData.email)}
                password={formData.password}
                onCancel={() => setDeviceLimitState(null)}
                onSignedIn={(accessToken) => {
                  setDeviceLimitState(null);
                  toast.success("Other device logged out. You're signed in.");
                  finishLoginWithToken(accessToken);
                }}
              />
            ) : !forgotPasswordMode ? (
              <>
                {/* Sign In Mode */}
                <div className="mt-2 md:mt-8 text-2xl font-semibold">
                  Sign In
                </div>
                <button
                  type="button"
                  onClick={() => googleLogin()}
                  disabled={uiLocked}
                  className={
                    darkMode
                      ? "input dark w-full lg:w-1/2 flex items-center justify-center gap-2"
                      : "input w-full lg:w-1/2 flex items-center justify-center gap-2"
                  }
                >
                  <FcGoogle className="text-xl" /> Continue with Google
                </button>
                <div className="flex items-center gap-4">
                  <div
                    className={`flex-1 h-px ${darkMode ? "bg-[#1E1E1E]" : "bg-[#CBCBCB]"}`}
                  ></div>
                  <span
                    className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    or
                  </span>
                  <div
                    className={`flex-1 h-px ${darkMode ? "bg-[#1E1E1E]" : "bg-[#CBCBCB]"}`}
                  ></div>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleLogin();
                  }}
                  className="flex flex-col gap-4"
                >
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      name="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      onKeyDown={blockSpaceKey}
                      onPaste={(e) =>
                        handlePasteStripSpaces(e, (value) => {
                          setFormData((prev) => ({ ...prev, email: value }));
                          setErrors((prev) => ({ ...prev, email: "" }));
                        })
                      }
                      onBlur={() =>
                        setFormData((prev) => ({
                          ...prev,
                          email: stripSpaces(prev.email),
                        }))
                      }
                      placeholder="Enter your email"
                      disabled={isAuthenticating}
                      className={
                        darkMode ? "input dark w-full" : "input w-full"
                      }
                    />

                    {errors.email && (
                      <p className="absolute top-9 right-0 text-[var(--primary)] text-xs font-medium mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      disabled={isAuthenticating}
                      className={
                        darkMode
                          ? "input dark w-full pr-10"
                          : "input w-full pr-10"
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isAuthenticating}
                      className="absolute right-3 top-0 bottom-0 my-auto h-fit text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible className="text-xl" />
                      ) : (
                        <AiOutlineEye className="text-xl" />
                      )}
                    </button>

                    {errors.password && (
                      <p className="absolute top-9 right-0 text-[var(--primary)] text-xs font-medium mt-1">
                        {errors.password}
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className={
                      isAuthenticating
                        ? "greenButton mt-2 mx-auto flex items-center justify-center gap-2"
                        : "greenButton mt-2 mx-auto"
                    }
                    disabled={isAuthenticating || loading}
                  >
                    {isAuthenticating ? (
                      <>
                        <span
                          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                          aria-hidden
                        />
                        <span>Signing in…</span>
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleForgotPasswordClick}
                    disabled={isAuthenticating}
                    className={`text-sm mx-auto hover:underline ${
                      darkMode
                        ? "text-gray-400 hover:text-gray-300"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    Forgot Password?
                  </button>
                </form>

                {/* Switch to Sign Up */}
                <div className="text-center mt-4">
                  <p
                    className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Don't have an account?{" "}
                    <button
                      type="button"
                      onClick={switchToSignUp}
                      disabled={isAuthenticating}
                      className="text-[var(--primary)] hover:underline font-medium"
                    >
                      Create a free account
                    </button>
                  </p>
                </div>
                {isAuthenticating && (
                  <AuthVerifyingOverlay
                    message="Signing you in…"
                    darkMode={darkMode}
                  />
                )}
              </>
            ) : (
              <>
                {/* Forgot Password Mode */}
                <div className="mt-2 md:mt-8">
                  <h2 className="text-2xl font-semibold">Reset Password</h2>
                  <p
                    className={`text-sm mt-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {forgotPasswordStep === 1 &&
                      "Enter your email to receive a verification code"}
                    {forgotPasswordStep === 2 &&
                      "Enter the 6-digit code sent to your email"}
                    {forgotPasswordStep === 3 && "Create your new password"}
                  </p>
                </div>

                {/* Step 1: Email Input */}
                {forgotPasswordStep === 1 && (
                  <div className="flex flex-col gap-4">
                    <input
                      type="text"
                      inputMode="email"
                      autoComplete="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(stripSpaces(e.target.value))}
                      onKeyDown={blockSpaceKey}
                      onPaste={(e) =>
                        handlePasteStripSpaces(e, setResetEmail)
                      }
                      onBlur={() => setResetEmail((prev) => stripSpaces(prev))}
                      placeholder="Enter your email"
                      className={
                        darkMode ? "input dark w-full" : "input w-full"
                      }
                    />
                    <button
                      onClick={handleSendOTP}
                      className="greenButton mt-2 mx-auto"
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Send OTP"}
                    </button>
                  </div>
                )}

                {/* Step 2: OTP Input */}
                {forgotPasswordStep === 2 && (
                  <div className="flex flex-col gap-4">
                    <div className="flex gap-2 justify-center">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength="1"
                          value={digit}
                          onChange={(e) =>
                            handleOtpChange(index, e.target.value)
                          }
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className={`w-12 h-12 text-center text-xl font-semibold ${
                            darkMode
                              ? "bg-[#1E1E1E] border-[#333] text-white"
                              : "bg-white border-gray-300 text-black"
                          } border-2 rounded-lg focus:border-[var(--primary)] focus:outline-none`}
                        />
                      ))}
                    </div>

                    {otpTimer > 0 && (
                      <p
                        className={`text-sm text-center ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Time remaining: {Math.floor(otpTimer / 60)}:
                        {String(otpTimer % 60).padStart(2, "0")}
                      </p>
                    )}

                    <button
                      onClick={handleVerifyOTP}
                      className="greenButton mt-2 mx-auto"
                      disabled={loading}
                    >
                      {loading ? "Verifying..." : "Verify OTP"}
                    </button>

                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={otpTimer > 0 || loading}
                      className={`text-sm mx-auto hover:underline ${
                        otpTimer > 0 || loading
                          ? "text-gray-500 cursor-not-allowed"
                          : darkMode
                            ? "text-gray-400 hover:text-gray-300"
                            : "text-gray-600 hover:text-gray-800"
                      }`}
                    >
                      Resend OTP
                    </button>
                  </div>
                )}

                {/* Step 3: New Password Input */}
                {forgotPasswordStep === 3 && (
                  <div className="flex flex-col gap-4">
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(stripSpaces(e.target.value))}
                        placeholder="Enter new password"
                        className={
                          darkMode
                            ? "input dark w-full pr-10"
                            : "input w-full pr-10"
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-0 bottom-0 my-auto h-fit text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        {showNewPassword ? (
                          <AiOutlineEyeInvisible className="text-xl" />
                        ) : (
                          <AiOutlineEye className="text-xl" />
                        )}
                      </button>
                    </div>

                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(stripSpaces(e.target.value))}
                        placeholder="Confirm new password"
                        className={
                          darkMode
                            ? "input dark w-full pr-10"
                            : "input w-full pr-10"
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-0 bottom-0 my-auto h-fit text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        {showConfirmPassword ? (
                          <AiOutlineEyeInvisible className="text-xl" />
                        ) : (
                          <AiOutlineEye className="text-xl" />
                        )}
                      </button>
                    </div>

                    <button
                      onClick={handleResetPassword}
                      className="greenButton mt-2 mx-auto"
                      disabled={loading}
                    >
                      {loading ? "Resetting..." : "Reset Password"}
                    </button>
                  </div>
                )}

                {/* Back to Sign In */}
                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  className={`text-sm mx-auto hover:underline ${
                    darkMode
                      ? "text-gray-400 hover:text-gray-300"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  ← Back to Sign In
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SignIn = (props) => (
  <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
    <SignInContent {...props} />
  </GoogleOAuthProvider>
);

export default SignIn;
